import React, { useCallback, useEffect, useState } from 'react';
import { User, Buildings, UploadSimple, ClockCounterClockwise, ArrowsClockwise, FileHtml } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { listOfficialAdhesionKits, downloadOfficialAdhesionKit } from '../../services/api';
import { KitPdfActions } from './KitPdfActions';
import { OfficialKitUploadDialog } from './OfficialKitUploadDialog';
import { OfficialKitHistoryDialog } from './OfficialKitHistoryDialog';
import { downloadOfficialHtml, saveBlob, digitalError } from '../../services/api/digitalAdhesion';

export const OfficialKitLibrary = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [upload, setUpload] = useState(null);
  const [history, setHistory] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const { data } = await listOfficialAdhesionKits(); setProfiles(data.items); }
    catch { setError('Les kits officiels sont momentanément indisponibles.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const html = async (version) => { try { const r = await downloadOfficialHtml(version.id); saveBlob(r.data, `kit-officiel-${version.kit_type}.html`); } catch (e) { setError(await digitalError(e)); } };
  return <section className="space-y-3 py-4 border-y border-[#E8E6E1]" data-testid="official-kit-library">
    <div className="flex flex-wrap justify-between items-center gap-2">
      <h2 className="text-base md:text-lg font-semibold text-[#1A2421]" data-testid="official-kit-library-title">Kits officiels en vigueur</h2>
      <span className="text-xs text-[#5E6D66]" data-testid="official-kit-library-access">Gestion super admin</span>
    </div>
    {loading && <p role="status" className="text-sm text-[#5E6D66]" data-testid="official-kit-library-loading">Chargement des kits officiels…</p>}
    {error && <div className="flex flex-wrap items-center gap-2">
      <p role="alert" className="text-sm text-red-700" data-testid="official-kit-library-error">{error}</p>
      <Button variant="outline" size="sm" onClick={load} data-testid="official-kit-library-retry"><ArrowsClockwise size={14} className="mr-2" /> Réessayer</Button>
    </div>}
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
      {profiles.map((profile) => {
        const type = profile.kit_type;
        const current = profile.current;
        const Icon = type === 'associe_pp' ? User : Buildings;
        return <Card key={type} className="border-[#E8E6E1] rounded-lg min-w-0" data-testid={`official-kit-card-${type}`}>
          <CardContent className="p-4 space-y-3">
            <div className="flex gap-3 items-start">
              <Icon size={24} className="shrink-0 text-[#2D5A4C]" weight="duotone" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-[#5E6D66]">Associé coopérateur</p>
                <h3 className="text-base font-semibold" data-testid={`official-kit-profile-${type}`}>{type === 'associe_pp' ? 'Personne physique' : 'Personne morale'}</h3>
              </div>
              <Badge variant="outline" className="shrink-0" data-testid={`official-kit-status-${type}`}>{current ? `Version ${current.version}` : 'À importer'}</Badge>
            </div>
            {current ? <div className="min-w-0">
              <p className="text-sm break-all font-medium" data-testid={`official-kit-filename-${type}`}>{current.filename}</p>
              <p className="text-xs text-[#5E6D66]" data-testid={`official-kit-blank-model-${type}`}>Modèle vierge · sans référence individuelle</p>
              <p className="text-xs text-[#5E6D66] mt-1" data-testid={`official-kit-metadata-${type}`}>En vigueur depuis le {new Date(current.created_at).toLocaleDateString('fr-FR')} · {current.page_count} page(s) · {(current.size / 1024).toFixed(0)} Ko</p>
            </div> : <p className="text-sm text-[#5E6D66]" data-testid={`official-kit-empty-${type}`}>Aucun PDF officiel en vigueur.</p>}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button variant="outline" size="sm" onClick={() => setUpload(profile)} disabled={loading} data-testid={`official-kit-import-${type}`}><UploadSimple size={16} className="mr-2" />{current ? 'Remplacer le PDF' : 'Importer le PDF'}</Button>
              {current && <div className="flex flex-wrap gap-1">
                {current.form_key && <Button size="sm" variant="outline" onClick={() => html(current)} data-testid={`official-kit-html-${type}`}><FileHtml size={16} className="mr-2" />HTML remplissable</Button>}
                <KitPdfActions kitId={current.id} scopeId={`official-${type}`} documentFilename={current.filename} documentRequest={(download) => downloadOfficialAdhesionKit(current.id, download)} />
                <Button size="sm" variant="ghost" onClick={() => setHistory(profile)} title="Historique des versions" aria-label="Historique des versions" data-testid={`official-kit-history-${type}`}><ClockCounterClockwise size={16} /></Button>
              </div>}
            </div>
          </CardContent>
        </Card>;
      })}
    </div>
    {upload && <OfficialKitUploadDialog profile={upload} onClose={() => setUpload(null)} onUploaded={async () => { await load(); setUpload(null); }} />}
    {history && <OfficialKitHistoryDialog profile={history} onClose={() => setHistory(null)} />}
  </section>;
};